/**
 * ChatConversation Component
 * Center area showing chat messages and input
 */

import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Bot, MessageSquare } from 'lucide-react';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Agent, ChatMessage, ChatActionConfirmation } from '../../types';
import { MessageBubble } from './MessageBubble';
import { ChatInputScreen } from './ChatInputScreen';
import { AvatarMedia } from './AvatarMedia';
import type { ChatExecutionMode } from '../../hooks/useChatAgent';
import type { RealtimeVoiceAgentState } from '../../hooks/useRealtimeVoiceAgent';
import { AI_AGENT_WARNING_MESSAGE_KEY } from '../../common/constants';
import { useReportBackgroundActivity } from '../../contexts/AppearanceContext';

interface ChatConversationProps {
  agent: Agent;
  messages: ChatMessage[];
  isLoading: boolean;
  isGenerating?: boolean;
  isInputDisabled?: boolean;
  onSendMessage: (content: string, files?: File[]) => void;
  onCancelResponse?: () => void;
  onConfirmAction?: (confirmation: ChatActionConfirmation) => Promise<void> | void;
  onCancelAction?: (confirmation: ChatActionConfirmation) => Promise<void> | void;
  executionMode: ChatExecutionMode;
  onExecutionModeChange: (mode: ChatExecutionMode) => void;
  voiceInputEnabled?: boolean;
  voiceAgent?: RealtimeVoiceAgentState;
  onToggleInfo: () => void;
  /** Current user's avatar URL */
  userAvatar?: string;
  /** Current user's avatar type */
  userAvatarType?: 'image' | 'video';
  sessionLimitWarning?: {
    level: 'near_limit' | 'limit_reached' | string;
    message: string;
    action?: {
      label: string;
      onClick: () => void;
    };
  } | null;
}

export const ChatConversation: React.FC<ChatConversationProps> = ({
  agent,
  messages,
  isLoading,
  isGenerating,
  isInputDisabled,
  onSendMessage,
  onCancelResponse,
  onConfirmAction,
  onCancelAction,
  executionMode,
  onExecutionModeChange,
  voiceInputEnabled = true,
  voiceAgent,
  onToggleInfo,
  userAvatar,
  userAvatarType,
  sessionLimitWarning,
}) => {
  const { t } = useTranslation();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  useReportBackgroundActivity(Boolean(isGenerating ?? isLoading));

  // Auto-scroll only the message list: scrollIntoView would also scroll overflow-hidden
  // ancestors and drag the whole chat (input included) upwards while the agent streams.
  useEffect(() => {
    const container = messagesContainerRef.current;
    container?.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleReportMessage = (message: ChatMessage) => {
    const messageIndex = messages.findIndex((item) => item.id === message.id);
    const previousUserMessage = [...messages.slice(0, messageIndex)]
      .reverse()
      .find((item) => item.role === 'user');

    window.dispatchEvent(
      new CustomEvent('feedback:open', {
        detail: {
          type: 'ABUSE_REPORT',
          title: t('feedback.chat.reportAiResponseTitle'),
          targetType: 'CHAT_MESSAGE',
          sourceContext: 'CHAT_MESSAGE_ACTION',
          conversationId: message.sessionId,
          messageId: message.id,
          chatconverd: agent.publicId || agent.id,
          reportedMessageSnapshot: message.content,
          previousUserMessageSnapshot: previousUserMessage?.content,
        },
      })
    );
  };

  const thinkingIndicator = (
    <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-3 dark:bg-slate-800">
      <CircularProgress size={16} className="text-gray-600 dark:text-slate-400" />
      <span className="text-sm text-gray-600 dark:text-slate-400">{t('chat.thinking')}</span>
    </div>
  );

  return (
    <div className="relative flex h-[calc(100dvh-4rem)] min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:h-[100dvh]">
      {sessionLimitWarning && (
        <div
          role="status"
          className={`flex flex-shrink-0 items-center justify-between gap-3 border-b px-4 py-2 text-sm ${
            sessionLimitWarning.level === 'limit_reached'
              ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200'
              : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200'
          }`}
        >
          <div className="flex min-w-0 items-center gap-2">
            <AlertTriangle size={16} className="flex-shrink-0" />
            <span className="min-w-0 flex-1">{sessionLimitWarning.message}</span>
          </div>
          {sessionLimitWarning.action && (
            <button
              type="button"
              onClick={sessionLimitWarning.action.onClick}
              className={`shrink-0 rounded px-2.5 py-1 text-xs font-semibold shadow-sm transition-colors ${
                sessionLimitWarning.level === 'limit_reached'
                  ? 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600'
                  : 'bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600'
              }`}
            >
              {sessionLimitWarning.action.label}
            </button>
          )}
        </div>
      )}

      {/* Messages Area - Flex 1, Scrollable */}
      <div
        ref={messagesContainerRef}
        className="ambient-surface min-h-0 flex-1 overflow-y-auto bg-gray-50 dark:bg-slate-900"
      >
        {messages.length === 0 ? (
          // Empty State - Centered in available space
          <div className="flex h-full items-center justify-center p-8">
            {isLoading ? (
              thinkingIndicator
            ) : (
              <div className="text-center">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800">
                  <MessageSquare size={32} className="text-gray-400 dark:text-slate-500" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-slate-100">
                  Start a conversation
                </h3>
                <p className="max-w-md text-sm text-gray-600 dark:text-slate-400">
                  Send a message to start chatting with {agent.name}. Your conversation will be
                  saved for future reference.
                </p>
              </div>
            )}
          </div>
        ) : (
          // Messages List - With proper padding
          <div className="w-full px-4 py-4">
            {messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                agentName={agent.name}
                agentAvatar={agent.avatarUrl ?? undefined}
                agentAvatarType={agent.avatarType}
                onAgentAvatarClick={onToggleInfo}
                userAvatar={userAvatar}
                userAvatarType={userAvatarType}
                onReport={handleReportMessage}
                assistantOffsetFromEnd={
                  messages
                    .slice(index + 1)
                    .filter(
                      (item) =>
                        item.role === 'agent' &&
                        item.status !== 'sending' &&
                        item.status !== 'failed'
                    ).length
                }
                onConfirmAction={onConfirmAction}
                onCancelAction={onCancelAction}
              />
            ))}

            {/* Loading Indicator */}
            {isLoading && (
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-indigo-500 to-purple-600">
                  <AvatarMedia
                    src={agent.avatarUrl}
                    type={agent.avatarType}
                    alt={agent.name}
                    fallback={<Bot size={16} className="text-white" />}
                  />
                </div>
                {thinkingIndicator}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Message Input - Fixed at Bottom */}
      <div className="sticky bottom-0 z-20 flex-shrink-0">
        <ChatInputScreen
          onSend={(content, files) => onSendMessage(content, files)}
          onCancel={onCancelResponse}
          isSubmitting={isInputDisabled ?? false}
          isGenerating={isGenerating ?? isLoading}
          placeholder={`Message ${agent.name}...`}
          executionMode={executionMode}
          onExecutionModeChange={onExecutionModeChange}
          voiceInputEnabled={voiceInputEnabled}
          voiceAgent={voiceAgent}
          showHeading={false}
          showAgentSelector={false}
          compact
          warningText={t(AI_AGENT_WARNING_MESSAGE_KEY)}
        />
      </div>
    </div>
  );
};
